<template>

  <section class="ni-section ni-depot" id="photo-depot">

    <div class="ni-container ni-container--wide">

      <SectionHeader

        variant="dark"

        :subtitle="t('newIndex.sections.photoDepot.subtitle')"

        more-href="/topic_pic"

      >

        <template #title>

          <span class="ni-section-header__title-mark">

            {{ t('newIndex.sections.photoDepot.title') }}

          </span>

        </template>

      </SectionHeader>



      <div class="ni-depot__teasers">

        <component
          :is="dailyScene ? 'div' : 'a'"
          id="teaser-card-daily"
          class="ni-depot__teaser"
          :href="dailyScene ? undefined : '/photo_grapher'"
          :role="dailyScene ? 'button' : undefined"
          :tabindex="dailyScene ? 0 : undefined"
          @click="dailyScene ? openLightbox(dailyScene) : undefined"
          @keydown.enter="dailyScene ? openLightbox(dailyScene) : undefined"
        >

          <div class="ni-depot__teaser-image">

            <img

              :src="dailyScene?.thumb || DAILY_PLACEHOLDER"

              :alt="dailyScene?.title || t('newIndex.sections.photoDepot.dailyView')"

            />

          </div>

          <div class="ni-depot__teaser-body">

            <h3 class="ni-depot__teaser-title">
              <img
                v-if="isChineseLocale"
                :src="newIndexTeaserIcons.daily"
                :alt="t('newIndex.sections.photoDepot.dailyView')"
                class="ni-depot__teaser-title-img"
              />
              <span v-else>{{ t('newIndex.sections.photoDepot.dailyView') }}</span>
            </h3>

          </div>

        </component>



        <component
          :is="weeklyPick ? 'div' : 'a'"
          id="teaser-card-weekly"
          class="ni-depot__teaser"
          :href="weeklyPick ? undefined : '/photo_grapher_album'"
          :role="weeklyPick ? 'button' : undefined"
          :tabindex="weeklyPick ? 0 : undefined"
          @click="weeklyPick ? openLightbox(weeklyPick) : undefined"
          @keydown.enter="weeklyPick ? openLightbox(weeklyPick) : undefined"
        >

          <div class="ni-depot__teaser-image">

            <img

              :src="weeklyPick?.thumb || WEEKLY_PLACEHOLDER"

              :alt="weeklyPick?.title || t('newIndex.sections.photoDepot.weeklyFeature')"

            />

          </div>

          <div class="ni-depot__teaser-body">

            <h3 class="ni-depot__teaser-title">
              <img
                v-if="isChineseLocale"
                :src="newIndexTeaserIcons.weekly"
                :alt="t('newIndex.sections.photoDepot.weeklyFeature')"
                class="ni-depot__teaser-title-img"
              />
              <span v-else>{{ t('newIndex.sections.photoDepot.weeklyFeature') }}</span>
            </h3>

          </div>

        </component>

      </div>



      <div class="ni-depot__deck">

        <div class="ni-depot__cats">

          <button

            id="depot-cat-btn-picks"

            type="button"

            class="ni-depot__cat-picks"

            :class="{ 'ni-depot__cat-picks--active': activeCategory === PICKS_CATEGORY }"

            @click="selectCategory(PICKS_CATEGORY)"

          >

            <span>{{ t('newIndex.sections.photoDepot.picks') }}</span>

            <img
              class="ni-depot__cat-picks-icon"
              src="@/assets/img/newIndex/ico-red-arraow.png"
              alt=""
              aria-hidden="true"
            />

          </button>



          <div class="ni-depot__cat-list">

            <button

              v-for="(cat, idx) in secondaryCategories"

              :id="`depot-cat-inner-btn-${idx}`"

              :key="cat.key"

              type="button"

              class="ni-depot__cat-btn"

              :class="{ 'ni-depot__cat-btn--active': activeCategory === cat.key }"

              @click="selectCategory(cat.key)"

            >

              {{ cat.label }}

            </button>

          </div>

        </div>



        <div

          class="ni-depot__carousel"

          @touchstart.passive="handleTouchStart"

          @touchend.passive="handleTouchEnd"

          @wheel.prevent="handleCarouselWheel"

        >

          <Transition name="ni-depot-carousel" mode="out-in">

            <div

              :key="`${activeCategory}-${carouselPage}`"

              class="ni-depot__gallery"

            >

              <div

                v-for="(photo, index) in displayPhotos"

                :id="`top-photo-wrapper-${getPhotoRank(index)}`"

                :key="photo.id"

                class="ni-depot__photo-wrap"

              >

                <a

                  :id="`top-photo-spot-${getPhotoRank(index)}`"

                  class="ni-depot__photo"

                  :href="getPhotoHref(photo)"

                >

                  <span class="ni-depot__photo-rank">TOP {{ getPhotoRank(index) }}</span>

                  <img :src="photo.imageUrl" :alt="photo.title" />

                  <span class="ni-depot__photo-play" aria-hidden="true">

                    <span class="ni-depot__photo-play-icon">
                      <img :src="newIndexArrowIcon" alt="" class="ni-icon-arrow ni-icon-arrow--play" />
                    </span>

                  </span>

                </a>

              </div>

            </div>

          </Transition>

        </div>



        <div class="ni-depot__pager">

          <button

            v-for="num in totalPages"

            :id="`depot-pager-dot-${num}`"

            :key="num"

            type="button"

            class="ni-depot__pager-dot"

            :class="{ 'ni-depot__pager-dot--active': num === carouselPage + 1 }"

            :aria-label="t('newIndex.sections.photoDepot.slideGroup', { n: num })"

            @click="goToPage(num - 1)"

          ></button>

          <button

            type="button"

            class="ni-depot__pager-next"

            :aria-label="t('newIndex.sections.photoDepot.next')"

            @click="goNextPage"

          >

            ›

          </button>

        </div>

      </div>

    </div>

    <PhotoLightbox
      :visible="lightboxVisible"
      :images="lightboxImages"
      :section-title="lightboxSectionTitle"
      :section-description="lightboxSectionDescription"
      :initial-index="0"
      @close="closeLightbox"
    />

  </section>

</template>



<script setup>

import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { SubjectList } from '@/api/common';
import { mapSubjectListToPhotos } from '@/utils/newIndexMappers';
import { newIndexArrowIcon, newIndexTeaserIcons } from '@/data/newIndexAssets.js';

import SectionHeader from './shared/SectionHeader.vue';
import PhotoLightbox from './shared/PhotoLightbox.vue';

const PICKS_CATEGORY = '__picks__';
const DAILY_PLACEHOLDER = 'https://images.unsplash.com/photo-1475503572774-15a45e5d60b9?auto=format&fit=crop&w=800&q=80';
const WEEKLY_PLACEHOLDER = 'https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?auto=format&fit=crop&w=800&q=80';

const props = defineProps({

  hotPhotos: {

    type: Array,

    default: () => [],

  },

  subjectCats: {

    type: Array,

    default: () => [],

  },

  dailyScene: {

    type: Object,

    default: null,

  },

  weeklyPick: {

    type: Object,

    default: null,

  },

});

const { t, locale } = useI18n();

const isChineseLocale = computed(() => ['zh', 'zhCHT'].includes(locale.value));

const DESKTOP_CAROUSEL_PAGE_SIZE = 5;
const MOBILE_CAROUSEL_PAGE_SIZE = 2;

const activeCategory = ref(PICKS_CATEGORY);
const carouselPage = ref(0);
const carouselPageSize = ref(DESKTOP_CAROUSEL_PAGE_SIZE);
const touchStartX = ref(0);
const lastWheelTime = ref(0);
const categoryPhotoCache = ref({});
const categoryLoading = ref(false);
const lightboxVisible = ref(false);
const lightboxImages = ref([]);
const lightboxSectionTitle = ref('');
const lightboxSectionDescription = ref('');

const secondaryCategories = computed(() =>
  props.subjectCats.map((cat) => ({
    key: cat.id,
    label: cat.title,
  }))
);

const categoryPhotos = computed(() => {
  if (activeCategory.value === PICKS_CATEGORY) {
    return props.hotPhotos || [];
  }

  return categoryPhotoCache.value[activeCategory.value] || [];
});

const totalPages = computed(() =>
  Math.max(1, Math.ceil(categoryPhotos.value.length / carouselPageSize.value))
);

const displayPhotos = computed(() => {
  const start = carouselPage.value * carouselPageSize.value;
  return categoryPhotos.value.slice(start, start + carouselPageSize.value);
});

function syncCarouselPageSize() {
  carouselPageSize.value = window.innerWidth <= 767
    ? MOBILE_CAROUSEL_PAGE_SIZE
    : DESKTOP_CAROUSEL_PAGE_SIZE;
}

let carouselMql = null;

onMounted(() => {
  syncCarouselPageSize();
  carouselMql = window.matchMedia('(max-width: 767px)');
  carouselMql.addEventListener('change', syncCarouselPageSize);
});

onBeforeUnmount(() => {
  carouselMql?.removeEventListener('change', syncCarouselPageSize);
  carouselMql = null;
});

watch(carouselPageSize, () => {
  carouselPage.value = Math.min(carouselPage.value, Math.max(0, totalPages.value - 1));
});

async function loadCategoryPhotos(catId) {
  if (categoryPhotoCache.value[catId]) return;

  categoryLoading.value = true;
  try {
    const res = await SubjectList({
      subtype: 1,
      subject_cate: parseInt(catId, 10),
      page: 1,
    });
    categoryPhotoCache.value = {
      ...categoryPhotoCache.value,
      [catId]: mapSubjectListToPhotos(res.data || []),
    };
  } catch {
    categoryPhotoCache.value = {
      ...categoryPhotoCache.value,
      [catId]: [],
    };
  } finally {
    categoryLoading.value = false;
  }
}

function getPhotoHref(photo) {
  return photo.detailUrl || `/topic_pic_detail?id=${encodeURIComponent(photo.id)}`;
}

function getPhotoRank(index) {
  return carouselPage.value * carouselPageSize.value + index + 1;
}

function openLightbox(section) {
  if (!section?.images?.length) return;
  lightboxImages.value = section.images;
  lightboxSectionTitle.value = section.title || '';
  lightboxSectionDescription.value = section.description || '';
  lightboxVisible.value = true;
}

function closeLightbox() {
  lightboxVisible.value = false;
}

function selectCategory(category) {
  activeCategory.value = category;
  carouselPage.value = 0;

  if (category !== PICKS_CATEGORY) {
    loadCategoryPhotos(category);
  }
}



function goToPage(pageIndex) {

  carouselPage.value = Math.min(Math.max(pageIndex, 0), totalPages.value - 1);

}



function goNextPage() {

  carouselPage.value = (carouselPage.value + 1) % totalPages.value;

}



function goPrevPage() {

  carouselPage.value = (carouselPage.value - 1 + totalPages.value) % totalPages.value;

}



function handleTouchStart(event) {
  touchStartX.value = event.touches[0]?.clientX ?? 0;
}



function handleTouchEnd(event) {

  const deltaX = event.changedTouches[0].clientX - touchStartX.value;

  if (Math.abs(deltaX) < 40) return;



  if (deltaX < 0) {

    goNextPage();

  } else {

    goPrevPage();

  }

}



function handleCarouselWheel(event) {

  const now = Date.now();

  if (now - lastWheelTime.value < 450) return;



  if (Math.abs(event.deltaY) > 8 || Math.abs(event.deltaX) > 8) {

    lastWheelTime.value = now;

    const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY)

      ? event.deltaX

      : event.deltaY;



    if (delta > 0) {

      goNextPage();

    } else {

      goPrevPage();

    }

  }

}

</script>


